/**
 * CopilotWidget — the floating "Ask Vooxy" button + chat panel.
 *
 * Visible on every authenticated page via Layout.js. Click the floating
 * button to toggle the panel. The panel has a left-rail session drawer
 * and a right-side message stream with input box. All logic lives in
 * `useCopilot`; this file is purely presentation + composition.
 *
 * UX principles:
 *   - **Zero-friction first use** — tapping the button lands directly on
 *     an empty "new conversation" with 4 suggested questions tuned to
 *     the user's role.
 *   - **Non-intrusive** — the button is 56px, bottom-right, never
 *     overlaps content.
 *   - **Honest about limits** — the rate-limit chip at the input shows
 *     "N/30 queries used this hour" so users know when they're close.
 */

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Sparkle, X, PaperPlaneTilt, Lightning } from '@phosphor-icons/react';
import useAuthStore from '../../store/authStore';
import useCopilot from './useCopilot';
import MessageBubble from './MessageBubble';
import SessionHistoryDrawer from './SessionHistoryDrawer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role-tuned starter prompts shown when the chat is empty. Keep each
// prompt short and immediately useful.
const SUGGESTIONS_BY_ROLE = {
  'Sales Executive': [
    'Which of my leads are overdue to follow up?',
    'Set a follow-up for next Monday on my newest lead.',
    'What is my win rate this quarter?',
    'Show my top 5 customers by lead value.',
  ],
  'Service Executive': [
    'List customers I visited in the last 30 days.',
    'Which service calls are overdue?',
    'How many interactions did I log this month?',
  ],
  'Regional Sales Manager': [
    'Which SE in my region has the highest win rate this quarter?',
    'List overdue collections in my region.',
    'How many channel-partner interactions were logged this week?',
    'Show quotes submitted but not approved yet.',
  ],
  'National Sales Manager': [
    'Compare win-rate across all regions this quarter.',
    'Reassign my oldest unworked lead to the RSM of that region.',
    'Who hasn\'t logged any interactions in the last 7 days?',
    'What\'s overdue > 60 days?',
  ],
  'Vice President': [
    'What is the gross margin this quarter?',
    'Which products are leaking margin this month?',
    'How many quotes are stuck in approval > 24 hours?',
    'Show YTD won revenue vs target.',
  ],
  'President': [
    'What is the gross margin this quarter?',
    'Which regions are missing their target this quarter?',
    'List overdue collections across all regions.',
    'Who is the top performer this month?',
  ],
  'Tender Executive': [
    'Which tenders close in the next 7 days?',
    'Which EMDs expire in the next 30 days?',
    'Which of my tenders are missing documents?',
    'How many tenders are awaiting feasibility approval?',
  ],
  'Sales Coordinator': [
    'List overdue collections across all regions.',
    'Show pending approvals I should flag.',
    'Who logged the most interactions this week?',
    'List quotes submitted but not approved yet.',
  ],
};


export default function CopilotWidget() {
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [draft, setDraft] = useState('');
  // Tenant-level kill switch — fetched once on mount. When disabled, we
  // render nothing so the FAB disappears entirely for all users on that
  // tenant.
  const [enabled, setEnabled] = useState(true);
  const [enabledChecked, setEnabledChecked] = useState(false);
  const token = useAuthStore((s) => s.token);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!token) { setEnabledChecked(true); return; }
    axios.get(`${API}/copilot/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setEnabled(r.data?.enabled !== false))
      .catch(() => setEnabled(true))  // default-on (safe for beta)
      .finally(() => setEnabledChecked(true));
  }, [token]);

  const {
    user,
    sessions,
    sessionId,
    messages,
    loadingHistory,
    sending,
    rateLimit,
    error,
    openSession,
    send,
    removeSession,
    confirmAction,
    cancelAction,
  } = useCopilot();

  // Auto-scroll to bottom whenever messages change or the panel opens.
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages, sending]);

  // Autofocus input when the panel opens.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const suggestions = SUGGESTIONS_BY_ROLE[user?.role] || SUGGESTIONS_BY_ROLE['Sales Executive'];

  const handleSend = () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft('');
    send(text);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Hide widget for the super-admin console — the hostname has no tenant
  // path (user?.is_super_admin) and the Copilot's RBAC assumes a tenant.
  if (!user || user.is_super_admin) return null;
  // Tenant kill-switch — don't render until we've checked, then only if on.
  if (!enabledChecked) return null;
  if (!enabled) return null;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[9000] w-14 h-14 rounded-full bg-emerald-600 text-white shadow-xl shadow-emerald-900/20 hover:bg-emerald-500 hover:shadow-emerald-900/30 transition-all flex items-center justify-center group"
          data-testid="copilot-fab"
          title="Ask Vooxy Copilot"
        >
          <Sparkle size={24} weight="fill" />
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 text-[9px] text-amber-900 font-bold flex items-center justify-center shadow">AI</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-[9000] bg-white rounded-2xl shadow-2xl shadow-emerald-900/20 flex overflow-hidden border border-slate-200"
          style={{ width: 'min(880px, calc(100vw - 48px))', height: 'min(640px, calc(100vh - 48px))' }}
          data-testid="copilot-panel"
        >
          {/* Session drawer */}
          {showHistory && (
            <div className="w-56 shrink-0 hidden md:flex">
              <SessionHistoryDrawer
                sessions={sessions}
                activeId={sessionId}
                onOpen={openSession}
                onDelete={removeSession}
                onNew={() => openSession(null)}
              />
            </div>
          )}

          {/* Main chat column */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-white">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center text-white">
                  <Sparkle size={14} weight="fill" />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Outfit' }}>Vooxy Copilot</div>
                  <div className="text-[10px] text-slate-500">Scoped to your login · Asks before any change</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowHistory((s) => !s)}
                  className="hidden md:inline-flex p-1.5 rounded hover:bg-slate-100 text-slate-500"
                  title="Toggle history"
                  data-testid="copilot-toggle-history"
                >
                  <Lightning size={14} />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
                  title="Close"
                  data-testid="copilot-close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Message stream */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-white" data-testid="copilot-message-stream">
              {loadingHistory ? (
                <div className="text-xs text-slate-400 italic">Loading conversation…</div>
              ) : messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                    <Sparkle size={28} weight="fill" className="text-emerald-600" />
                  </div>
                  <div className="text-lg font-bold text-slate-800 mb-1" style={{ fontFamily: 'Outfit' }}>Ask me anything about your CRM</div>
                  <div className="text-xs text-slate-500 mb-5 max-w-sm">I only see what your role is allowed to see. Try a question:</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => send(s)}
                        className="text-left text-xs px-3 py-2 rounded-lg border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 text-slate-700 transition"
                        data-testid={`copilot-suggestion-${i}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((m, i) => (
                  <MessageBubble key={m.id || i} msg={m} onConfirmAction={confirmAction} onCancelAction={cancelAction} />
                ))
              )}

              {sending && (
                <div className="flex items-center gap-2 text-xs text-slate-500 italic" data-testid="copilot-thinking">
                  <Sparkle size={12} weight="fill" className="animate-pulse text-emerald-500" />
                  Thinking…
                </div>
              )}
              {error && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="copilot-error">
                  {error}
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-slate-200 p-3 bg-slate-50/60">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={sending ? 'Waiting for reply…' : 'Ask or instruct — e.g. "Add a note to the Fortis lead"'}
                  className="flex-1 resize-none text-sm px-3 py-2 rounded-lg border border-slate-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none max-h-32"
                  disabled={sending}
                  data-testid="copilot-input"
                />
                <button
                  onClick={handleSend}
                  disabled={sending || !draft.trim()}
                  className="shrink-0 w-9 h-9 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed flex items-center justify-center"
                  data-testid="copilot-send"
                  title="Send (Enter)"
                >
                  <PaperPlaneTilt size={16} weight="fill" />
                </button>
              </div>
              <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-400 px-1">
                <span>Press Enter to send · Shift+Enter for newline</span>
                <span data-testid="copilot-ratelimit">
                  {rateLimit.used}/{rateLimit.limit} queries this hour
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
